import { convertIsoDate } from "@src/helpers/text-services/convertDate";

/** Частые пресеты форматирования дат в бизнес-UI. */
export const BUSINESS_DATE_PRESETS = {
    /** `dd.MM.yyyy`, например `28.06.2026`. */
    date: "dd.MM.yyyy",
    /** `dd.MM.yy`, например `28.06.26`. */
    dateShort: "dd.MM.yy",
    /** `dd.MM.yyyy hh:mm`, дата и время без секунд. */
    dateTime: "dd.MM.yyyy hh:mm",
    /** `dd.MM.yyyy hh.mm.ss`, дата и время с секундами через точки. */
    dateTimeSeconds: "dd.MM.yyyy hh.mm.ss",
    /** `hh:mm`, только время. */
    time: "hh:mm",
} as const;

/** Имя пресета из {@link BUSINESS_DATE_PRESETS}. */
export type BusinessDatePreset = keyof typeof BUSINESS_DATE_PRESETS;

/** Опции {@link formatBusinessDateText}. */
export interface FormatBusinessDateTextOptions {
    /** Пресет формата; по умолчанию `date`. */
    preset?: BusinessDatePreset;
    /** Явный формат вместо пресета; передаётся в {@link convertIsoDate}. */
    format?: string;
    /** Текст для пустой или невалидной даты; по умолчанию пустая строка. */
    fallback?: string;
}

function isEmptyDate(value: string | null | undefined): value is null | undefined {
    return value == null || value.trim() === "";
}

function resolveFormat(options?: FormatBusinessDateTextOptions | BusinessDatePreset): string {
    if (typeof options === "string") {
        return BUSINESS_DATE_PRESETS[options];
    }

    if (options?.format != null && options.format !== "") {
        return options.format;
    }

    return BUSINESS_DATE_PRESETS[options?.preset ?? "date"];
}

function resolveFallback(options?: FormatBusinessDateTextOptions | BusinessDatePreset): string {
    if (typeof options === "string") {
        return "";
    }

    return options?.fallback ?? "";
}

function isValidDate(value: string): boolean {
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp);
}

/**
 * Формирует plain-text представление ISO-даты для бизнес-UI. \
 * Пустые значения возвращают `fallback` (по умолчанию `""`).
 * @param date ISO-дата или дата-время
 * @param options пресет, явный формат и fallback
 * @returns отформатированная дата или fallback
 */
export function formatBusinessDateText(
    date: string | null | undefined,
    options?: FormatBusinessDateTextOptions | BusinessDatePreset,
): string {
    const fallback = resolveFallback(options);

    if (isEmptyDate(date)) {
        return fallback;
    }

    const normalizedDate = date.trim();

    if (!isValidDate(normalizedDate)) {
        return fallback;
    }

    return convertIsoDate(normalizedDate, resolveFormat(options));
}

/** Опции {@link formatBusinessDateRangeText}. */
export interface FormatBusinessDateRangeTextOptions extends FormatBusinessDateTextOptions {
    /** Разделитель между датами. @default " - " */
    separator?: string;
}

/**
 * Формирует plain-text диапазон дат.
 * @param startDate начало диапазона
 * @param endDate конец диапазона
 * @param options пресет, формат, fallback и разделитель
 * @returns строка вида `01.01.2026 - 31.01.2026`
 */
export function formatBusinessDateRangeText(
    startDate: string | null | undefined,
    endDate: string | null | undefined,
    options?: FormatBusinessDateRangeTextOptions | BusinessDatePreset,
): string {
    const normalizedOptions = typeof options === "string" ? { preset: options } : options;
    const separator = normalizedOptions?.separator ?? " - ";
    const startText = formatBusinessDateText(startDate, normalizedOptions);
    const endText = formatBusinessDateText(endDate, normalizedOptions);

    if (startText === "" && endText === "") {
        return normalizedOptions?.fallback ?? "";
    }

    if (startText === "") {
        return endText;
    }

    if (endText === "") {
        return startText;
    }

    return `${startText}${separator}${endText}`;
}
